import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import {
  AUTONOMY_LEVEL_LABEL, AUTONOMY_LEVELS, BUILTIN_ROLES, DATA_SCOPE_LABEL, PERMISSION_GROUPS, ROLE_TIER_LABEL,
  TIER_DATA_SCOPES, tierPermissions, type DataScope, type PermissionKey, type UserRole,
} from "@usales/contract/values";
import { api, can, type Department, type Me, type Member, type RoleView } from "../api.ts";
import { fmtDate } from "../labels.ts";
import { AutonomyControl } from "../components/AutonomyControl.tsx";
import { BackNavigation, EmptyState, ErrorState, LoadingState, SectionTabs, SectionPanel } from "../components/Workspace.tsx";
import "./org.css";
const EnterpriseInformation=lazy(()=>import("../components/EnterpriseInformation.tsx").then(module=>({default:module.EnterpriseInformation})));

/**
 * 组织与权限(原型 F01–F07):部门树、成员的角色与数据范围、角色权限矩阵,再加每个人的 AI 权限。
 *
 * 三层权限各管一件事,界面上也分开讲:
 *   · 角色的**功能权限点**:能用哪些功能(接口那边按 route-permissions.ts 拦)
 *   · **数据范围**:看得见哪些客户(仅本人 / 本部门 / 及下级 / 全部),本部门按部门树算
 *   · **AI 上限**:这个角色下 AI 最多做到哪一档;在上限内仍靠采纳率自己挣
 *
 * 账号仍然只能由管理者开,没有公开注册。停用前要先把在跟的客户转走 ——
 * 不然客户会挂在一个不再登录的人名下,动作卡照样生成、照样推给他,而没有人看得见。
 */
type Section = "enterprise" | "structure" | "members" | "roles" | "autonomy" | "export";

const memberScope = (m: Member): DataScope => m.dataScope ?? m.roleDataScope ?? BUILTIN_ROLES[m.role].dataScope;
const memberRoleName = (m: Member): string => m.roleName ?? BUILTIN_ROLES[m.role].name;

export function Org(props: { me: Me; initialSection?:"members" }): React.JSX.Element {
  const canMembers = can(props.me, "org.member");
  const canDepartments = can(props.me, "org.department");
  const canRoles = can(props.me, "org.role");
  // 导出不按数据范围裁剪,后端只认管理员档;前端同样只给管理员看入口
  const canExport = can(props.me, "data.export") && props.me.role === "admin";
  const sections = useMemo(() => ([
    ["enterprise", "企业信息"] as const,
    ...(canDepartments || canMembers ? [["structure", "组织架构"] as const] : []),
    ...(canMembers ? [["members", "成员与角色"] as const] : []),
    ...(canRoles || canMembers ? [["roles", "角色与权限"] as const] : []),
    ...(canMembers ? [["autonomy", "AI 权限与冻结"] as const] : []),
    ...(canExport ? [["export", "数据导出"] as const] : []),
  ]), [canDepartments, canMembers, canRoles, canExport]);
  const [section, setSection] = useState<Section>(props.initialSection&&sections.some(([k])=>k===props.initialSection)?props.initialSection:"enterprise");
  const [members, setMembers] = useState<Member[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [roles, setRoles] = useState<RoleView[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const load = useCallback(() => {
    setErr("");
    void Promise.all([
      canMembers ? api.get<{ users: Member[] }>("/api/manager/users").then((r) => setMembers(r.users)) : Promise.resolve(),
      canDepartments||canMembers?api.get<{ departments: Department[] }>("/api/org/departments").then((r) => setDepartments(r.departments)):Promise.resolve(),
      canRoles||canMembers?api.get<{ roles: RoleView[] }>("/api/org/roles").then((r) => setRoles(r.roles)):Promise.resolve(),
    ]).catch((e) => setErr(String((e as Error).message))).finally(() => setLoading(false));
  }, [canMembers,canDepartments,canRoles]);
  useEffect(load, [load]);

  const run = async (fn: () => Promise<void>, done?: string): Promise<boolean> => {
    setErr(""); setOk("");
    try { await fn(); if (done) setOk(done); load(); return true; }
    catch (e) { setErr((e as Error).message); return false; }
  };

  if (loading) return <LoadingState label="正在加载组织与权限" />;
  if (err && !members.length && !departments.length && !roles.length) return <ErrorState message={err} onRetry={load} />;
  return (
    <>
      {section!=="enterprise"&&<div className="hint">这里定的是<b>人的权限边界</b>:谁能用哪些功能、看得见哪些客户。在这个边界内,AI 仍按采纳率在 L0–L4 里逐级挣权限,但<b>不会超过角色设定的 AI 上限</b>。</div>}
      {err && <div className="err" role="alert">{err}</div>}
      {ok && <div className="ok" role="status">{ok}</div>}
      <SectionTabs label="组织与权限" options={sections} value={section} onChange={(s) => { setSection(s); setOk(""); setErr(""); }} />
      <div className="org-tabs-panel">
        <SectionPanel active={section === "enterprise"}><Suspense fallback={<LoadingState label="正在读取企业信息"/>}><EnterpriseInformation isAdmin={props.me.role==="admin"}/></Suspense></SectionPanel>
        <SectionPanel active={section === "structure"}><Structure me={props.me} departments={departments} members={members} canEdit={canDepartments} run={run} /></SectionPanel>
        <SectionPanel active={section === "members"}><Members me={props.me} members={members} departments={departments} roles={roles} run={run} /></SectionPanel>
        <SectionPanel active={section === "roles"}><Roles me={props.me} roles={roles} canEdit={canRoles} run={run} /></SectionPanel>
        <SectionPanel active={section === "autonomy"}><AutonomyControl users={members} canEdit={props.me.role === "admin"} /></SectionPanel>
        {canExport && <SectionPanel active={section === "export"}><DataExport /></SectionPanel>}
      </div>
    </>
  );
}

type Run = (fn: () => Promise<void>, done?: string) => Promise<boolean>;

/* ───────── 数据导出 ───────── */

/** 直接用链接下载:会话在 httpOnly cookie 里,同源请求自动带上,不用先 fetch 再拼 Blob。 */
function DataExport(): React.JSX.Element {
  return (
    <div className="card">
      <div className="secttl"><span>导出企业数据</span></div>
      <p>把本企业的数据打包成一个 ZIP,每类数据一张表格(CSV,Excel / WPS 可直接打开):</p>
      <ul className="export-list">
        <li>客户、联系人</li>
        <li>沟通记录(含转写全文)、阶段变化、行动建议</li>
        <li>知识库资料(含全文)</li>
        <li>成员与角色</li>
      </ul>
      <div className="btns"><a className="b p" href="/api/org/export" download>下载 ZIP</a></div>
      <div className="hint">录音和原始文件不在包里;沟通的转写全文在「沟通记录」表中。导出包含全部客户的联系方式,请妥善保管。每次导出都会记录导出人和时间。</div>
    </div>
  );
}

/* ───────── F01 / F02 组织架构 ───────── */

function descendants(departments: Department[], id: string): Set<string> {
  const out = new Set([id]);
  for (let grew = true; grew;) {
    grew = false;
    for (const d of departments) if (d.parentId && out.has(d.parentId) && !out.has(d.id)) { out.add(d.id); grew = true; }
  }
  return out;
}

function Structure(props: { me: Me; departments: Department[]; members: Member[]; canEdit: boolean; run: Run }): React.JSX.Element {
  const [form, setForm] = useState<{ id?: string; name: string; parentId: string; leaderId: string }>();
  const blocked = form?.id ? descendants(props.departments, form.id) : new Set<string>();
  const save = async (): Promise<void> => {
    if (!form) return;
    const body = { name: form.name.trim(), parentId: form.parentId || null, leaderId: form.leaderId || null };
    const done = await props.run(() => form.id ? api.patch(`/api/org/departments/${form.id}`, body) : api.post("/api/org/departments", body),
      form.id ? `已保存「${body.name}」` : `已创建部门「${body.name}」`);
    if (done) setForm(undefined);
  };
  return (
    <div className="card">
      <div className="secttl"><span>部门树(负责人 · 在职人数)</span>
        {props.canEdit && <button className="b sm p" onClick={() => setForm(form && !form.id ? undefined : { name: "", parentId: "", leaderId: "" })}>＋ 新增部门</button>}
      </div>
      {form && (
        <div className="chatbox">
          <div className="ttl">{form.id ? "编辑部门" : "新增部门"}</div>
          <div className="form-grid">
            <label>部门名称<input className="in" aria-label="部门名称" value={form.name} maxLength={60} placeholder="如:华东一组" onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
            <label>上级部门
              <select className="in" aria-label="上级部门" value={form.parentId} onChange={(e) => setForm({ ...form, parentId: e.target.value })}>
                <option value="">无(顶级部门)</option>
                {props.departments.filter((d) => !blocked.has(d.id)).map((d) => <option key={d.id} value={d.id}>{"　".repeat(d.depth)}{d.name}</option>)}
              </select>
            </label>
            <label>部门负责人
              <select className="in" aria-label="部门负责人" value={form.leaderId} onChange={(e) => setForm({ ...form, leaderId: e.target.value })}>
                <option value="">暂不指定</option>
                {props.members.filter((m) => m.active).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </label>
          </div>
          <div className="hint">上级部门决定汇报关系,也决定「本部门及下级」数据范围能看到谁。负责人只是标注,不自动带来权限 —— 权限看他的角色和数据范围。</div>
          <div className="btns"><button className="b p" disabled={!form.name.trim()} onClick={() => void save()}>保存</button><button className="b q" onClick={() => setForm(undefined)}>取消</button></div>
        </div>
      )}
      {props.departments.length === 0 ? <EmptyState title="还没有部门" description="建好部门再把成员放进去,就可以按「本部门 / 及下级」给主管设数据范围。" icon="users" />
        : props.departments.map((d) => (
          <div key={d.id} className="org-node" style={{ paddingLeft: 6 + d.depth * 22 }}>
            <span className="org-twig">{d.depth === 0 ? "🏢" : "└"}</span>
            <span className="org-name" style={{ fontWeight: d.depth === 0 ? 700 : 600 }}>{d.name}</span>
            <span className="org-lead">负责人:{d.leaderName ?? "未指定"}</span>
            <span className="tag tN">{d.members} 人</span>
            {props.canEdit && <div className="btns">
              <button className="b sm q" onClick={() => setForm({ id: d.id, name: d.name, parentId: d.parentId ?? "", leaderId: d.leaderId ?? "" })}>编辑</button>
              <button className="b sm q danger-text" onClick={() => void props.run(() => api.del(`/api/org/departments/${d.id}`), `已删除「${d.name}」,原成员已回到未分配部门`)}>删除</button>
            </div>}
          </div>
        ))}
    </div>
  );
}

/* ───────── F03 / F04 成员与角色 ───────── */

function Members(props: { me: Me; members: Member[]; departments: Department[]; roles: RoleView[]; run: Run }): React.JSX.Element {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "sales" as Member["role"], password: "" });
  const [assign, setAssign] = useState<{ member: Member; roleId: string; dataScope: string; departmentId: string }>();
  const roleOf = (id: string): RoleView | undefined => props.roles.find((r) => r.id === id);
  const aiCap = (m: Member): number => roleOf(m.roleId ?? m.role)?.aiCap ?? 4;
  const chosen = assign ? roleOf(assign.roleId) : undefined;
  const scopes = chosen ? TIER_DATA_SCOPES[chosen.baseRole] : [];

  const add = async (): Promise<void> => {
    if (await props.run(() => api.post("/api/manager/users", form), "账号已开通,对方现在就能登录")) {
      setForm({ email: "", name: "", role: "sales", password: "" }); setAdding(false);
    }
  };
  const confirm = async (): Promise<void> => {
    if (!assign) return;
    const m = assign.member;
    const body = {
      ...(assign.roleId !== (m.roleId ?? m.role) || assign.dataScope !== (m.dataScope ?? "") ? { roleId: assign.roleId, dataScope: assign.dataScope || null } : {}),
      ...(assign.departmentId !== (m.departmentId ?? "") ? { departmentId: assign.departmentId || null } : {}),
    };
    if (!Object.keys(body).length) { setAssign(undefined); return; }
    if (await props.run(() => api.patch(`/api/manager/users/${m.id}/access`, body), `已调整「${m.name}」的角色、数据范围与部门`)) setAssign(undefined);
  };

  return (
    <div className="card">
      <div className="secttl"><span>成员({props.members.filter((r) => r.active).length} 人在职)· 部门 / 角色 / 数据范围</span>
        <button className="b sm p" onClick={() => setAdding(!adding)}>＋ 开账号</button>
      </div>
      {adding && (
        <div className="chatbox">
          <div className="ttl">开一个账号 <span>— 系统没有公开注册,账号一律由管理者开;开通后再「分配」部门与角色;对方首次登录要先改掉初始口令</span></div>
          <div className="form-row row">
            <input className="in" aria-label="邮箱" placeholder="邮箱" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input className="in" aria-label="姓名" placeholder="姓名" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <select className="in" aria-label="角色" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as Member["role"] })}>
              <option value="sales">销售</option><option value="manager">管理者</option>
              {props.me.role === "admin" && <option value="admin">管理员</option>}
            </select>
            <input className="in" type="password" aria-label="初始口令" placeholder="初始口令(≥8 位)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <button className="b p" disabled={!form.email || !form.name || form.password.length < 8} onClick={() => void add()}>开通</button>
          </div>
          {props.me.role !== "admin" && <div className="hint">只有管理员能开管理员账号 —— 否则管理者和管理员的区别只是多点一下鼠标。</div>}
        </div>
      )}
      <div className="table-scroll"><table className="tbl">
        <thead><tr><th>姓名</th><th>部门</th><th>角色</th><th>数据范围</th><th>AI 上限</th><th>在跟客户</th><th>年目标</th><th>状态</th><th></th></tr></thead>
        <tbody>{props.members.map((m) => (
          <tr key={m.id} style={{ opacity: m.active ? 1 : 0.5, ...(assign?.member.id === m.id ? { background: "var(--blue-bg)" } : {}) }}>
            <td><b>{m.name}</b>{m.id === props.me.userId && <span className="muted"> (你)</span>}<div className="muted">{m.email}</div></td>
            <td>{m.departmentName ?? <span className="muted">未分配</span>}</td>
            <td><span className={`tag ${m.roleId ? "tW" : "tN"}`}>{memberRoleName(m)}</span></td>
            <td>{DATA_SCOPE_LABEL[memberScope(m)]}{m.dataScope && <span className="muted" title="单独为这个人设置,不跟角色走"> · 单独设置</span>}</td>
            <td className="muted">L{aiCap(m)}</td>
            <td>{m.open > 0 ? <span className="tag tA">{m.open}</span> : <span className="muted">0</span>}</td>
            <td><AnnualTarget user={m} onSaved={() => void props.run(async () => {})} /></td>
            <td><span className={`tag ${m.active ? "tG" : "tN"}`}>{m.active ? "在职" : "已停用"}</span><div className="muted">{fmtDate(m.createdAt)} 加入</div></td>
            <td><div className="btns">
              {m.id !== props.me.userId && <button className="b sm q" onClick={() => setAssign(assign?.member.id === m.id ? undefined : { member: m, roleId: m.roleId ?? m.role, dataScope: m.dataScope ?? "", departmentId: m.departmentId ?? "" })}>分配</button>}
              {m.id !== props.me.userId && <button className="b sm" onClick={() => void props.run(() => api.patch(`/api/manager/users/${m.id}`, { active: !m.active }), m.active ? `${m.name} 已停用,会话已一起收掉` : `${m.name} 已恢复`)}>{m.active ? "停用" : "恢复"}</button>}
            </div></td>
          </tr>
        ))}</tbody>
      </table></div>

      {assign && (
        <div className="chatbox">
          <div className="ttl">给「{assign.member.name}」分配角色、数据范围与部门</div>
          <div className="form-grid">
            <label>角色
              <select className="in" aria-label="分配角色" value={assign.roleId} onChange={(e) => setAssign({ ...assign, roleId: e.target.value, dataScope: "" })}>
                {props.roles.filter((r) => props.me.role === "admin" || r.baseRole !== "admin").map((r) => <option key={r.id} value={r.id}>{r.name}{r.builtin ? "(预置)" : ""}</option>)}
              </select>
            </label>
            <label>数据可见范围
              <select className="in" aria-label="数据可见范围" value={assign.dataScope} onChange={(e) => setAssign({ ...assign, dataScope: e.target.value })}>
                <option value="">跟随角色({chosen ? DATA_SCOPE_LABEL[chosen.dataScope] : "—"})</option>
                {scopes.map((s) => <option key={s} value={s}>{DATA_SCOPE_LABEL[s]}</option>)}
              </select>
            </label>
            <label>所属部门
              <select className="in" aria-label="所属部门" value={assign.departmentId} onChange={(e) => setAssign({ ...assign, departmentId: e.target.value })}>
                <option value="">未分配部门</option>
                {props.departments.map((d) => <option key={d.id} value={d.id}>{"　".repeat(d.depth)}{d.name}</option>)}
              </select>
            </label>
          </div>
          <div className="hint">角色「{chosen?.name}」决定他的功能权限与 <b>AI 上限 L{chosen?.aiCap ?? 4}</b>;数据范围决定他看得见哪些客户,「本部门 / 及下级」按所属部门算,没有部门就只看自己。{chosen?.baseRole === "sales" && "销售档只看自己负责的客户。"}改完立刻生效,不用重新登录。</div>
          <div className="btns"><button className="b p" onClick={() => void confirm()}>确认分配</button><button className="b q" onClick={() => setAssign(undefined)}>取消</button></div>
        </div>
      )}
      <div className="hint">停用前要先把他手上<b>在跟的客户</b>转走(交接或重新分配)—— 不然客户会挂在一个不再登录的人名下:动作卡照样生成、照样推给他,而没有人看得见。停用会立刻收掉他的会话。你不能调整自己的角色,也不能调整权限比你大的成员。</div>
    </div>
  );
}

/* ───────── F05 / F06 / F07 角色与权限 ───────── */

interface RoleDraft { id?: string; name: string; baseRole: UserRole; permissions: PermissionKey[]; dataScope: DataScope; aiCap: number; description: string }

function Roles(props: { me: Me; roles: RoleView[]; canEdit: boolean; run: Run }): React.JSX.Element {
  const [view, setView] = useState<{ kind: "list" } | { kind: "matrix"; roleId: string } | { kind: "new" }>({ kind: "list" });
  if (view.kind === "matrix") {
    const role = props.roles.find((r) => r.id === view.roleId);
    // 预置角色只有管理员能调,而且管理员档不能调(调坏了没人能改回来)——后端同样拦
    const editable = props.canEdit && (!role?.builtin || (props.me.role === "admin" && role.baseRole !== "admin"));
    if (role) return <RoleEditor key={role.id} me={props.me} roles={props.roles} canEdit={editable} run={props.run}
      initial={{ id: role.id, name: role.name, baseRole: role.baseRole, permissions: [...role.permissions], dataScope: role.dataScope, aiCap: role.aiCap, description: role.description ?? "" }}
      builtin={role.builtin} customized={role.customized} onPick={(id) => setView({ kind: "matrix", roleId: id })} onDone={() => setView({ kind: "list" })} />;
  }
  if (view.kind === "new") {
    const base = props.roles.find((r) => r.id === "sales")!;
    return <RoleEditor me={props.me} roles={props.roles} canEdit run={props.run} builtin={false}
      initial={{ name: "", baseRole: base.baseRole, permissions: [...base.permissions], dataScope: base.dataScope, aiCap: 1, description: "" }}
      onDone={() => setView({ kind: "list" })} />;
  }
  return (
    <div className="card">
      <div className="secttl"><span>角色({props.roles.length})· 系统预置 + 自定义</span>
        {props.canEdit && <button className="b sm p" onClick={() => setView({ kind: "new" })}>＋ 新建角色</button>}
      </div>
      <div className="table-scroll"><table className="tbl">
        <thead><tr><th>角色</th><th>类型</th><th>工作台</th><th>数据范围</th><th>AI 上限</th><th>成员</th><th>说明</th><th></th></tr></thead>
        <tbody>{props.roles.map((r) => (
          <tr key={r.id}>
            <td><b>{r.name}</b></td>
            <td><span className={`tag ${r.builtin ? "tN" : "tW"}`}>{r.builtin ? "系统" : "自定义"}</span>{r.customized && <span className="tag tA" style={{ marginLeft: 4 }}>已调整</span>}</td>
            <td>{ROLE_TIER_LABEL[r.baseRole]}</td>
            <td>{DATA_SCOPE_LABEL[r.dataScope]}</td>
            <td><span className={`tag ${r.aiCap === 4 ? "tG" : r.aiCap <= 1 ? "tW" : "tA"}`}>L{r.aiCap}</span></td>
            <td>{r.members}</td>
            <td className="muted">{r.customized ? "本企业调整过,与系统默认不同" : r.description}</td>
            <td><div className="btns">
              <button className="b sm q" onClick={() => setView({ kind: "matrix", roleId: r.id })}>权限</button>
              {props.canEdit && !r.builtin && <button className="b sm q danger-text" onClick={() => void props.run(() => api.del(`/api/org/roles/${r.id}`), `已删除角色「${r.name}」`)}>删除</button>}
            </div></td>
          </tr>
        ))}</tbody>
      </table></div>
      <div className="hint">预置的「管理者」「销售」可以由管理员直接调整权限,随时能恢复默认;「管理员」不能调,也不能删预置角色。想要多一种岗位(如实习销售),就新建角色,以预置角色为基准复制再改。自定义角色挂在某个工作台上,<b>只能在那个工作台允许的权限里勾选</b> —— 只能比基准收紧,不会越级。{!props.canEdit && "角色的增删改需要「角色与权限管理」权限。"}</div>
    </div>
  );
}

function RoleEditor(props: {
  me: Me; roles: RoleView[]; initial: RoleDraft; builtin: boolean; customized?: boolean; canEdit: boolean; run: Run;
  onDone: () => void; onPick?: (id: string) => void;
}): React.JSX.Element {
  const [draft, setDraft] = useState<RoleDraft>(props.initial);
  const isNew = !props.initial.id;
  const allowed = new Set(tierPermissions(draft.baseRole));
  const has = (key: PermissionKey): boolean => draft.permissions.includes(key);
  const toggle = (key: PermissionKey): void => setDraft({ ...draft, permissions: has(key) ? draft.permissions.filter((p) => p !== key) : [...draft.permissions, key] });
  const changed = JSON.stringify(draft) !== JSON.stringify(props.initial);
  const copyFrom = (id: string): void => {
    const r = props.roles.find((x) => x.id === id);
    if (r) setDraft({ ...draft, baseRole: r.baseRole, permissions: [...r.permissions], dataScope: r.dataScope, aiCap: r.aiCap });
  };
  const save = async (): Promise<void> => {
    const body = { name: draft.name.trim(), permissions: draft.permissions, dataScope: draft.dataScope, aiCap: draft.aiCap, description: draft.description.trim() || null };
    const done = await props.run(() => isNew ? api.post("/api/org/roles", { ...body, baseRole: draft.baseRole })
      : props.builtin ? api.put(`/api/org/builtin-roles/${props.initial.id}`, { permissions: body.permissions, dataScope: body.dataScope, aiCap: body.aiCap })
      : api.patch(`/api/org/roles/${props.initial.id}`, body),
      isNew ? `已创建角色「${body.name}」· ${body.permissions.length} 项权限 · AI 上限 L${body.aiCap}` : `已保存「${body.name}」的权限`);
    if (done) props.onDone();
  };
  const reset = async (): Promise<void> => {
    if (await props.run(() => api.del(`/api/org/builtin-roles/${props.initial.id}`), `「${props.initial.name}」已恢复系统默认`)) props.onDone();
  };
  return (
    <div className="card">
      <BackNavigation label="返回角色列表" onBack={props.onDone}/>
      <div className="secttl"><span>{isNew ? "＋ 新建自定义角色" : <>权限矩阵 · {props.initial.name} <span className={`tag ${props.builtin ? "tN" : "tW"}`}>{props.builtin ? "系统" : "自定义"}</span></>}</span>
        <div className="btns">
          {props.onPick && <select className="in" aria-label="切换角色" value={props.initial.id} onChange={(e) => props.onPick?.(e.target.value)}>{props.roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>}

        </div>
      </div>
      {props.canEdit && (
        <div className="form-grid">
          <label>角色名称<input className="in" aria-label="角色名称" value={draft.name} maxLength={30} placeholder="如:实习销售 / 区域经理" disabled={props.builtin} title={props.builtin ? "预置角色不能改名" : undefined} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></label>
          {isNew
            ? <label>以现有角色为基准(复制其权限再改)<select className="in" aria-label="基准角色" defaultValue="sales" onChange={(e) => copyFrom(e.target.value)}>{props.roles.filter((r) => props.me.role === "admin" || r.baseRole !== "admin").map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select></label>
            : <label>工作台<input className="in" value={ROLE_TIER_LABEL[draft.baseRole]} disabled /></label>}
          <label>数据可见范围<select className="in" aria-label="角色数据范围" value={draft.dataScope} onChange={(e) => setDraft({ ...draft, dataScope: e.target.value as DataScope })}>{TIER_DATA_SCOPES[draft.baseRole].map((s) => <option key={s} value={s}>{DATA_SCOPE_LABEL[s]}</option>)}</select></label>
          {!props.builtin && <label>说明<input className="in" aria-label="角色说明" value={draft.description} maxLength={200} placeholder="如:新人保护,AI 封顶 L1" onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></label>}
        </div>
      )}
      {props.canEdit && isNew && !draft.name.trim() && <div className="hint">角色名称不能为空。</div>}
      <div className="secttl"><span>功能权限点(已选 {draft.permissions.length} 项){props.canEdit ? " · 灰色的是这个工作台不能有的" : ""}</span></div>
      {PERMISSION_GROUPS.map((g) => (
        <div key={g.key} className="perm-group">
          <div className="perm-title">{g.label}</div>
          <div className="perm-cells">{g.permissions.map((p) => (
            <button key={p.key} type="button" className={`perm-cell ${has(p.key) ? "on" : ""}`} aria-pressed={has(p.key)}
              disabled={!props.canEdit || !allowed.has(p.key)} title={!allowed.has(p.key) ? `「${ROLE_TIER_LABEL[draft.baseRole]}」工作台不能有这项权限` : undefined}
              onClick={() => toggle(p.key)}><span className="perm-box">{has(p.key) ? "✓" : ""}</span>{p.label}</button>
          ))}</div>
        </div>
      ))}
      <div className="perm-group ai-row">
        <div className="perm-title">AI 动作自主度上限<span className="muted" style={{ fontWeight: 400 }}> · 这个角色下 AI 最多自己做到哪一档</span></div>
        <div className="perm-cells">{AUTONOMY_LEVELS.map((lv) => (
          <button key={lv} type="button" className={`perm-cell ai ${draft.aiCap === lv ? "on" : ""}`} aria-pressed={draft.aiCap === lv} disabled={!props.canEdit}
            onClick={() => setDraft({ ...draft, aiCap: lv })}><b>L{lv}</b> {AUTONOMY_LEVEL_LABEL[lv]}</button>
        ))}</div>
      </div>
      <div className="hint">管理员定上限,AI 在上限内靠采纳率自己往上挣 —— 例如「实习销售」封顶 L1,做得再稳也只到「只建议」。金钱和合同类动作无论怎么设都恒为 L1。{props.builtin && (props.canEdit
        ? "改的是本企业所有用这个预置角色的人,保存后立刻生效;随时可以恢复系统默认。"
        : props.initial.baseRole === "admin" ? "管理员角色不能调整 —— 删掉角色管理权限后,没有人能再改回来。" : "预置角色只有管理员能调整。")}</div>
      {props.canEdit && (
        <div className="btns">
          <button className="b p" disabled={!changed || !draft.name.trim()} onClick={() => void save()}>{isNew ? "创建角色" : changed ? "保存改动" : "未改动"}</button>
          {!isNew && <button className="b q" disabled={!changed} onClick={() => setDraft(props.initial)}>还原</button>}
          {props.builtin && props.customized && <button className="b q" onClick={() => void reset()}>恢复系统默认</button>}
        </div>
      )}
    </div>
  );
}

/**
 * 年度业绩目标。销售的「行动导航 → 业绩统计」按它算完成率。
 *
 * **留空 = 不考核**,不是 0 —— 设成 0 会让完成率除零。所以清空走的是
 * 「把目标置为 null」那条路,而不是存一个 0 进去。
 */
function AnnualTarget(props: { user: Member; onSaved: () => void }): React.JSX.Element {
  const [v, setV] = useState(props.user.annualTarget ?? "");
  const [busy, setBusy] = useState(false);
  const dirty = (props.user.annualTarget ?? "") !== v;

  const save = async (): Promise<void> => {
    setBusy(true);
    try {
      await api.patch(`/api/manager/users/${props.user.id}`, { annualTarget: v.trim() === "" ? "" : Number(v) });
      props.onSaved();
    } finally { setBusy(false); }
  };

  return (
    <div className="row" style={{ gap: 4 }}>
      <input className="in sm" style={{ width: 96 }} aria-label={`${props.user.name}的年目标`} placeholder="不考核" value={v} disabled={busy}
        onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && dirty) void save(); }} />
      {dirty && <button className="b sm p" disabled={busy} onClick={() => void save()}>{busy ? "…" : "存"}</button>}
    </div>
  );
}
